/* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from 'react';
import { StatusSignal } from '../../design-system/StatusSignal';
import { onboardingProvider, type Account, type Subject } from '../client-onboarding/provider';
import { mediaOpportunityProvider, type MediaOpportunityProvider, type OpportunityDetail, type OpportunityDraft, type OpportunityItem, type RecipientDimension, type RecipientProfileInput, type RecipientVerificationInput, type VerificationState } from './provider';
import {ActionPlanWorkspace} from './ActionPlanWorkspace';
const label = (v: string) => v.replaceAll('_', ' ').toUpperCase();
const tone = (v: string) => v === 'verified' || v === 'strong_fit' || v === 'ready_for_human_action' ? 'positive' : v === 'expired' || v === 'withdrawn' || v === 'not_a_fit' || v === 'conflicted' || v === 'revoked' ? 'urgent' : v === 'unverified' || v.includes('needs') ? 'warning' : 'neutral';
const dims: Record<RecipientDimension, string> = { identity: 'Identity', current_role: 'Current role / association', editorial_interest: 'Editorial interest', contact_method: 'Contact method' };
export function MediaOpportunityWorkspace({ provider = mediaOpportunityProvider, directory = onboardingProvider }: {
    provider?: MediaOpportunityProvider;
    directory?: {
        accounts(): Promise<Account[]>;
        subjects(id: string): Promise<Subject[]>;
    };
}) {
    const [items, setItems] = useState<OpportunityItem[]>([]), [selected, setSelected] = useState<OpportunityDetail | null>(null), [search, setSearch] = useState(''), [state, setState] = useState(''), [loading, setLoading] = useState(true), [error, setError] = useState(''), [creating, setCreating] = useState(false);
    const load = async () => {
        try {
            setLoading(true);
            const value = await provider.list({ search, state });
            setItems(value.items);
            if (value.items.length && !selected)
                setSelected(await provider.detail(value.items[0].id));
            setError('');
        }
        catch (e) {
            setError((e as Error).message);
        }
        finally {
            setLoading(false);
        }
    };
    useEffect(() => { void load(); }, [search, state]);
    const open = async (id: string) => setSelected(await provider.detail(id));
    const refresh = async () => {
        if (selected)
            setSelected(await provider.detail(selected.opportunity.id));
        await load();
    };
    return <main className="media-opportunity-workspace">
<header className="moi-heading">
<div>
<h1>Opportunity Decision Desk</h1>
<p>Separate editorial fit from verified operational readiness. Every next step remains evidence-backed and human-approved.</p>
</div>
<button className="button button--primary" onClick={() => setCreating(true)}>ADD OPPORTUNITY</button>
</header>{error && <div className="moi-alert" role="alert">{error}</div>}<section className="moi-toolbar">
<label>Search<input value={search} onChange={e => setSearch(e.target.value)} placeholder="Title, outlet, or topic"/>
</label>
<label>Lifecycle<select value={state} onChange={e => setState(e.target.value)}>
<option value="">All states</option>{['needs_verification', 'active', 'matched', 'under_review', 'ready_for_human_action', 'expired', 'withdrawn', 'closed'].map(v => <option key={v}>{v}</option>)}</select>
</label>
<span>{loading ? 'Loading…' : `${items.length} opportunities`}</span>
</section>
<div className="moi-layout">
<section className="moi-inventory">{items.map(item => <button key={item.id} className={selected?.opportunity.id === item.id ? 'is-selected' : ''} onClick={() => void open(item.id)}>
<span>
<b>{item.title}</b>
<small>{item.outlet_program}</small>
</span>
<span>
<StatusSignal label={label(item.verification_state)} tone={tone(item.verification_state)}/>
<small>{item.deadline ? new Date(item.deadline).toLocaleDateString() : 'No deadline'}</small>
</span>
<span>
<b>{label(item.match_classification ?? 'not_evaluated')}</b>
<small>{item.recipient_name ?? 'Recipient incomplete'}</small>
</span>
</button>)}</section>{selected ? <Desk value={selected} provider={provider} refresh={refresh}/> : <aside className="moi-detail moi-placeholder">
<h2>Select an opportunity</h2>
</aside>}</div>{creating && <OpportunityForm provider={provider} directory={directory} close={() => setCreating(false)} created={async (id) => { setCreating(false); await load(); await open(id); }}/>}</main>;
}
const currentOperationalBlockers = (v: OpportunityDetail) => {
    const o = v.opportunity, match = v.matches[0];
    if (o.verification_state !== 'verified')
        return ['Opportunity source requires verification.'];
    if (o.lifecycle_status === 'expired' || o.lifecycle_status === 'withdrawn' || Boolean(o.deadline && new Date(o.deadline).getTime() < Date.now()))
        return ['Opportunity is expired or withdrawn.'];
    if (o.identity_state !== 'verified')
        return ['Recipient identity requires current verification.'];
    if (o.role_state !== 'verified')
        return ['Recipient current role or association requires verification.'];
    if (o.editorial_interest_state !== 'verified')
        return ['Recipient editorial interest requires verification.'];
    if (o.contact_state !== 'verified')
        return ['Recipient contact method requires verification.'];
    if (match?.invalidated_at)
        return ['Evidence changed; re-evaluate fit before review.'];
    if (match?.readiness_state === 'blocked' && match.blocker_snapshot.blockers.length)
        return ['The latest match assessment recorded unresolved requirements; re-evaluate fit to confirm the current state.'];
    return [];
};
const recipientRequiresVerification = (v: OpportunityDetail) => {
    const o = v.opportunity;
    return !o.recipient_id || [o.identity_state, o.role_state, o.editorial_interest_state, o.contact_state].some(state => state !== 'verified');
};
const readinessProjection = (value: OpportunityDetail) => {
    const o = value.opportunity, match = value.matches[0];
    const expired = o.lifecycle_status === 'expired' || o.lifecycle_status === 'withdrawn' || Boolean(o.deadline && new Date(o.deadline).getTime() < Date.now());
    const operationalBlockers = currentOperationalBlockers(value);
    if (expired)
        return { readiness: 'expired', operationalBlockers, approval: 'NOT ELIGIBLE', action: 'REVIEW OPPORTUNITY STATUS', target: 'history' };
    if (o.verification_state !== 'verified')
        return { readiness: 'blocked', operationalBlockers, approval: 'BLOCKED', action: 'VERIFY OPPORTUNITY', target: 'request' };
    if (recipientRequiresVerification(value))
        return { readiness: 'blocked', operationalBlockers, approval: match?.invalidated_at ? 'PRIOR APPROVAL INVALIDATED' : 'BLOCKED', action: 'VERIFY RECIPIENT', target: 'recipient' };
    if (match?.invalidated_at)
        return { readiness: 'blocked', operationalBlockers, approval: 'PRIOR APPROVAL INVALIDATED', action: 'RESOLVE CURRENT BLOCKER', target: 'match' };
    if (!match)
        return { readiness: 'blocked', operationalBlockers: ['Editorial fit has not been evaluated.'], approval: 'NOT YET ELIGIBLE', action: 'RE-EVALUATE FIT', target: 'match' };
    if (operationalBlockers.length)
        return { readiness: 'blocked', operationalBlockers, approval: 'BLOCKED', action: 'RESOLVE CURRENT BLOCKER', target: 'match' };
    if (match.readiness_state === 'ready_for_human_action')
        return { readiness: 'ready_for_human_action', operationalBlockers, approval: 'OWNER / ADMIN APPROVED', action: 'REVIEW INTERNAL ACTION PLAN', target: 'history' };
    return { readiness: 'needs_human_review', operationalBlockers, approval: 'OWNER / ADMIN APPROVAL PENDING', action: 'APPROVE READINESS', target: 'moi-review' };
};
function Desk({ value, provider, refresh }: {
    value: OpportunityDetail;
    provider: MediaOpportunityProvider;
    refresh(): Promise<void>;
}) {
    const o = value.opportunity, m = value.matches[0], brief = value.eligibleBriefs[0], [notice, setNotice] = useState(''), [drawer, setDrawer] = useState(false), [planOpen,setPlanOpen]=useState(false), [rationale, setRationale] = useState('Evidence and editorial request align.'), [angle, setAngle] = useState(m?.preferred_angle_position ?? 1);
    const verify = async (s: VerificationState) => { await provider.verify(o.id, s, `Operator recorded ${s} after manual review.`); await refresh(); };
    const evaluate = async () => {
        if (brief) {
            await provider.match(o.id, brief.id);
            await refresh();
        }
    };
    const review = async (decision: string) => {
        if (m)
            try {
                await provider.review(m.id, { decision, rationale, preferredAnglePosition: angle });
                setNotice('Human decision saved. No outreach was initiated.');
                await refresh();
            }
            catch (e) {
                setNotice((e as Error).message);
            }
    };
    const projection = readinessProjection(value);
    const jumpTo = (target: string) => document.getElementById(target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    const doAction = () => projection.action === 'VERIFY OPPORTUNITY' ? void verify('verified') : projection.action === 'VERIFY RECIPIENT' ? setDrawer(true) : projection.action === 'RE-EVALUATE FIT' ? void evaluate() : projection.action==='REVIEW INTERNAL ACTION PLAN'?setPlanOpen(true):document.getElementById(projection.target)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    return <aside className="moi-detail">
<header>
<div>
<span>{label(o.opportunity_type)}</span>
<h2>{o.title}</h2>
<p>{o.outlet_program}</p>
</div>
<StatusSignal label={label(o.lifecycle_status)} tone={tone(o.lifecycle_status)}/>
</header>{notice && <div className="moi-notice" role="status">{notice}</div>}<section className="moi-decision-summary">
<div>
<span>Editorial fit</span>
<b>{label(m?.classification ?? 'not_evaluated')}</b>
</div>
<div>
<span>Operational readiness</span>
<b>{label(projection.readiness)}</b>
</div>
<div className="moi-decision-blocker">
<span>Current operational blockers</span>
<b>{projection.operationalBlockers.length ? projection.operationalBlockers.join(' ') : projection.readiness === 'ready_for_human_action' ? 'Editorial requirements satisfied. No current operational blockers.' : 'Editorial requirements satisfied. Operational readiness awaits human approval.'}</b>
</div>
<div>
<span>Human approval status</span>
<b>{projection.approval}</b>
</div>
<div className="moi-next-action">
<span>Next required action</span>
<button className="button button--primary" onClick={doAction}>{projection.action}</button>
</div>
</section>
<nav className="moi-jump">
<button type="button" onClick={() => jumpTo('request')}>Request</button>
<button type="button" onClick={() => jumpTo('recipient')}>Recipient</button>
<button type="button" onClick={() => jumpTo('match')}>Match</button>
<button type="button" onClick={() => jumpTo('moi-review')}>Decision</button>
<button type="button" onClick={() => jumpTo('history')}>History</button>
<button type="button" onClick={() => setPlanOpen(true)}>Action Plan</button>
</nav>
<section className="moi-action-plan-callout"><div><h3>Internal Action Plan</h3><p>Prepare owners, approved angles, materials, tasks, deadlines, and human approvals. No plan action sends outreach.</p></div><button className="button button--primary" disabled={!m||!brief||!provider.actionPlan} onClick={()=>setPlanOpen(true)}>{projection.readiness==='ready_for_human_action'?'OPEN ACTION PLAN':'CREATE DRAFT ACTION PLAN'}</button></section>
<section id="request">
<h3>Editorial request</h3>
<p>{o.editorial_request}</p>
<dl>
<div>
<dt>Observed</dt>
<dd>{new Date(o.observed_at).toLocaleString()}</dd>
</div>
<div>
<dt>Deadline</dt>
<dd>{o.deadline ? new Date(o.deadline).toLocaleString() : 'Not supplied'}</dd>
</div>
<div>
<dt>Source</dt>
<dd>{o.source_description}</dd>
</div>
<div>
<dt>Verification</dt>
<dd>{label(o.verification_state)}</dd>
</div>
</dl>
<button className="button button--secondary" onClick={() => void verify('verified')}>MARK VERIFIED</button>
</section>
<section id="recipient">
<div className="moi-section-heading">
<div>
<h3>Recipient verification</h3>
<p>{o.recipient_name ?? 'NO RECIPIENT'}</p>
</div>
<button className="button button--primary" onClick={() => setDrawer(true)}>{o.recipient_id ? 'EDIT & VERIFY RECIPIENT' : 'ADD RECIPIENT'}</button>
</div>
<div className="moi-dimensions">{[['Identity', o.identity_state], ['Current role', o.role_state], ['Editorial interest', o.editorial_interest_state], ['Contact method', o.contact_state]].map(([k, v]) => <span key={k}>
<b>{k}</b>{label(v ?? 'unverified')}</span>)}</div>
<p className="moi-boundary">NO VERIFIED RELATIONSHIP PATH. Contact verification never authorizes outreach.</p>
</section>
<section id="match">
<div className="moi-section-heading">
<div>
<h3>Explainable Brief match</h3>
<p>{brief ? `Approved Brief v${brief.version_number}: ${brief.content.headline}` : 'No approved Brief is eligible.'}</p>
</div>
<button className="button button--secondary" disabled={!brief} onClick={() => void evaluate()}>{m ? 'RE-EVALUATE FIT' : 'EVALUATE FIT'}</button>
</div>{m ? <Match match={m} currentBlockers={projection.operationalBlockers} readiness={projection.readiness}/> : <p>Not evaluated.</p>}</section>{m && <section id="moi-review">
<h3>Human decision</h3>{brief && <label>Preferred editorial angle<select value={angle} onChange={e => setAngle(Number(e.target.value))}>{brief.content.angles.map((a, i) => <option value={i + 1} key={a.headline}>{a.headline}</option>)}</select>
</label>}<label>Rationale<textarea value={rationale} onChange={e => setRationale(e.target.value)}/>
</label>
<div className="moi-review-actions">
<button className="button button--secondary" onClick={() => void review('relevant')}>RELEVANT</button>
<button className="button button--secondary" onClick={() => void review('research_requested')}>RESEARCH REQUIRED</button>
<button className="button button--tertiary" onClick={() => void review('deferred')}>DEFER</button>
<button className="button button--primary" disabled={Boolean(m.blocker_snapshot.blockers.length) || Boolean(m.invalidated_at)} onClick={() => void review('ready_for_human_action')}>READY FOR HUMAN ACTION</button>
</div></section>}<section id="history">
<h3>Decision history</h3>
<p className="moi-history-context">Persisted events for {o.title}{o.recipient_name ? ` · ${o.recipient_name}` : ''}.</p>
<h4>Opportunity history</h4>
{value.lifecycleHistory.length ? <ol className="moi-timeline">{value.lifecycleHistory.map((e, i) => <li key={i}>
<b>{e.from_status ? `${label(e.from_status)} → ${label(e.to_status)}` : label(e.to_status)}</b>
<span><strong>Lifecycle event</strong> · {e.reason}<small>Operator: {e.operator_display_name ?? (e.operator_id ? `ID ${e.operator_id}` : 'Actor metadata unavailable')}</small></span>
<time>{new Date(e.occurred_at).toLocaleString()}</time>
</li>)}</ol> : <p>No lifecycle events are available.</p>}
<h4>Readiness decisions</h4>
{m?.decisions?.length ? <ol className="moi-timeline">{m.decisions.map((d, i) => <li key={i}>
<b>{label(d.decision)}</b>
<span><strong>Readiness decision</strong> · {d.rationale}<small>Operator: {d.reviewer_display_name ?? (d.reviewer_operator_id ? `ID ${d.reviewer_operator_id}` : 'Actor metadata unavailable')}</small></span>
<time>{new Date(d.decided_at).toLocaleString()}</time>
</li>)}</ol> : <p>No readiness decisions are available.</p>}
</section>{drawer && <RecipientDrawer value={value} provider={provider} close={() => setDrawer(false)} saved={async () => { setDrawer(false); await refresh(); }}/>} {planOpen&&<ActionPlanWorkspace opportunity={value} provider={provider} close={()=>setPlanOpen(false)}/>}</aside>;
}
function Match({ match: m, currentBlockers, readiness }: {
    match: OpportunityDetail['matches'][number];
    currentBlockers: string[];
    readiness: string;
}) { const x = m.explanation, limitations = x.limitations ?? [...x.missingRequirements, ...x.cautions]; return <div className="moi-match">
<header>
<StatusSignal label={label(m.classification)} tone={tone(m.classification)}/>
<StatusSignal label={label(readiness)} tone={tone(readiness)}/>
</header>
<div className="moi-explanation-grid">
<section>
<h4>Why this matches</h4>
<p><b>Editorial requirement:</b> {x.whyThisMatch.editorialRequirement ?? 'The recorded editorial request.'}</p>
<p><b>Approved Story:</b> {x.whyThisMatch.approvedStory}</p>
<p><b>Selected angle:</b> {x.whyThisMatch.primaryAngle}</p>
<p>{x.whyThisMatch.rationale ?? 'Current accepted intelligence supports review of this match.'}</p>
<dl>
<div>
<dt>Audience</dt>
<dd>{x.whyThisMatch.audience}</dd>
</div>
<div>
<dt>Format</dt>
<dd>{x.whyThisMatch.format}</dd>
</div>
</dl>
<h4>Supporting accepted facts</h4>
{x.supportingFacts.length ? <ul>{x.supportingFacts.map(v => <li key={v}>{v}</li>)}</ul> : <p>No relevant current accepted evidence is available.</p>}
</section>
<section>
<h4>Current operational blockers</h4>
{currentBlockers.length ? <ul className="moi-blockers">{currentBlockers.map(v => <li key={v}>{v}</li>)}</ul> : <p>{readiness === 'ready_for_human_action' ? 'Editorial requirements satisfied. No current operational blockers.' : 'Editorial requirements satisfied. Operational readiness remains blocked until Owner/Admin approval.'}</p>}
<h4>Match-time assessment</h4>
{m.blocker_snapshot.blockers.length ? <ul>{m.blocker_snapshot.blockers.map(v => <li key={v}>{v}</li>)}</ul> : <p>No blockers were recorded when this match was evaluated.</p>}
<h4>Limitations</h4>
{limitations.length ? <ul>{limitations.map(v => <li key={v}>{v}</li>)}</ul> : <p>No additional limitations recorded.</p>}
</section>
</div>
</div>; }
function RecipientDrawer({ value, provider, close, saved }: {
    value: OpportunityDetail;
    provider: MediaOpportunityProvider;
    close(): void;
    saved(): Promise<void>;
}) {
    const o = value.opportunity, [error, setError] = useState(''), [profile, setProfile] = useState<RecipientProfileInput>({ recipientKind: o.recipient_kind ?? 'editorial_desk', displayName: o.recipient_name ?? '', outletProgram: o.recipient_outlet_program ?? o.outlet_program, currentRole: o.recipient_role ?? '', editorialDesk: o.editorial_desk ?? '', geography: o.recipient_geography ?? '', language: o.recipient_language ?? '', editorialTopics: o.editorial_topics ?? '', preferredFormats: o.preferred_formats ?? '', professionalProfileUrl: o.professional_profile_url ?? '', contactMethod: o.contact_method ?? '', notes: o.recipient_notes ?? '' }), [v, setV] = useState<RecipientVerificationInput>({ dimension: 'identity', claim: '', evidenceDescription: '', observedAt: new Date().toISOString().slice(0, 16), verificationState: 'verified', freshnessDays: 365 });
    useEffect(() => {
        const returnFocus = document.querySelector<HTMLElement>('.moi-next-action button');
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                close();
                return;
            }
            if (event.key === 'Tab') {
                const controls = [...document.querySelectorAll<HTMLElement>('.moi-recipient-drawer button:not(:disabled), .moi-recipient-drawer input:not(:disabled), .moi-recipient-drawer select:not(:disabled), .moi-recipient-drawer textarea:not(:disabled), .moi-recipient-drawer a[href]')].filter(element => element.offsetParent !== null || element === document.activeElement);
                if (!controls.length)
                    return;
                const first = controls[0], last = controls[controls.length - 1];
                if (event.shiftKey && document.activeElement === first) {
                    event.preventDefault();
                    last.focus();
                }
                else if (!event.shiftKey && document.activeElement === last) {
                    event.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => { document.removeEventListener('keydown', onKeyDown); returnFocus?.focus(); };
    }, []);
    const set = (k: keyof RecipientProfileInput, x: string) => setProfile(p => ({ ...p, [k]: x }));
    const save = async () => {
        try {
            await provider.saveRecipient(o.id, profile);
            await saved();
        }
        catch (e) {
            setError((e as Error).message);
        }
    };
    const record = async () => {
        try {
            await provider.verifyRecipient(o.id, { ...v, observedAt: new Date(v.observedAt).toISOString() });
            await saved();
        }
        catch (e) {
            setError((e as Error).message);
        }
    };
    return <div className="moi-drawer-backdrop">
<section className="moi-recipient-drawer" role="dialog" aria-modal="true" aria-labelledby="recipient-title">
<header>
<div>
<h2 id="recipient-title">Recipient verification</h2>
<p>Verify every dimension independently with recorded evidence.</p>
</div>
<button autoFocus className="button button--tertiary" onClick={close}>CLOSE</button>
</header><div className="moi-drawer-context" aria-label="Recipient verification overview">
<strong>{o.recipient_name ?? 'Recipient profile not yet named'}</strong>
<span>{o.recipient_role ?? o.recipient_kind ?? 'Recipient role not yet recorded'} · {o.recipient_outlet_program ?? o.outlet_program}</span>
<div className="moi-dimensions">{[['Identity', o.identity_state], ['Current role', o.role_state], ['Editorial interest', o.editorial_interest_state], ['Contact method', o.contact_state]].map(([k, state]) => <span key={k}><b>{k}</b>{label(state ?? 'unverified')}</span>)}</div>
</div>{error && <div className="moi-alert" role="alert">{error}</div>}<div className="moi-drawer-scroll">
<section>
<h3>Recipient profile</h3>
<div className="moi-form-grid">
<label>Recipient type<select value={profile.recipientKind} onChange={e => set('recipientKind', e.target.value)}>{['journalist', 'editor', 'producer', 'podcast_host', 'editorial_desk', 'program', 'outlet'].map(x => <option key={x}>{x}</option>)}</select>
</label>
<label>Display name<input value={profile.displayName} onChange={e => set('displayName', e.target.value)}/>
</label>
<label>Outlet / program<input value={profile.outletProgram} onChange={e => set('outletProgram', e.target.value)}/>
</label>
<label>Current role / association<input value={profile.currentRole} onChange={e => set('currentRole', e.target.value)}/>
</label>
<label>Editorial desk<input value={profile.editorialDesk} onChange={e => set('editorialDesk', e.target.value)}/>
</label>
<label>Geography<input value={profile.geography} onChange={e => set('geography', e.target.value)}/>
</label>
<label>Language<input value={profile.language} onChange={e => set('language', e.target.value)}/>
</label>
<label>Preferred formats<input value={profile.preferredFormats} onChange={e => set('preferredFormats', e.target.value)}/>
</label>
</div>
<label>Editorial topics<textarea value={profile.editorialTopics} onChange={e => set('editorialTopics', e.target.value)}/>
</label>
<label>Public professional profile URL<input value={profile.professionalProfileUrl} onChange={e => set('professionalProfileUrl', e.target.value)}/>
</label>
<label>Contact-method description<input value={profile.contactMethod} onChange={e => set('contactMethod', e.target.value)}/>
</label>
<button className="button button--secondary" onClick={() => void save()}>SAVE PROFILE VERSION</button>
</section>
<section>
<h3>Verification evidence</h3>
<div className="moi-form-grid">
<label>Dimension<select value={v.dimension} onChange={e => setV({ ...v, dimension: e.target.value as RecipientDimension, freshnessDays: e.target.value === 'identity' ? 365 : e.target.value === 'editorial_interest' ? 180 : 90 })}>{Object.entries(dims).map(([k, n]) => <option value={k} key={k}>{n}</option>)}</select>
</label>
<label>Decision<select value={v.verificationState} onChange={e => setV({ ...v, verificationState: e.target.value as RecipientVerificationInput['verificationState'] })}>{['verified', 'needs_recheck', 'conflicted', 'expired', 'revoked', 'unverified'].map(x => <option key={x}>{x}</option>)}</select>
</label>
<label>Observed<input type="datetime-local" value={v.observedAt} onChange={e => setV({ ...v, observedAt: e.target.value })}/>
</label>
<label>Freshness days<input type="number" min="1" max="730" value={v.freshnessDays} onChange={e => setV({ ...v, freshnessDays: Number(e.target.value) })}/>
</label>
</div>
<label>Claim being verified<input value={v.claim} onChange={e => setV({ ...v, claim: e.target.value })}/>
</label>
<label>Evidence description<textarea value={v.evidenceDescription} onChange={e => setV({ ...v, evidenceDescription: e.target.value })}/>
</label>
<label>Public source URL<input value={v.sourceUrl ?? ''} onChange={e => setV({ ...v, sourceUrl: e.target.value })}/>
</label>
<label>Relevant excerpt<textarea value={v.evidenceExcerpt ?? ''} onChange={e => setV({ ...v, evidenceExcerpt: e.target.value })}/>
</label>
<button className="button button--primary" disabled={!v.claim || !v.evidenceDescription} onClick={() => void record()}>RECORD VERIFICATION DECISION</button>
</section>
<section>
<h3>Evidence history</h3>
<ol className="moi-timeline">{value.recipientVerificationHistory.map(r => <li key={r.id}>
<b>{dims[r.dimension]} · {label(r.verification_state)}</b>
<span>{r.claim} — {r.evidence_description}</span>
<time>{new Date(r.verified_at).toLocaleString()} · {r.freshness_days} day window · {r.verifier_display_name ?? (r.verified_by_operator_id ? `ID ${r.verified_by_operator_id}` : 'Actor metadata unavailable')}</time>
</li>)}</ol>
</section>
</div>
</section>
</div>;
}
function OpportunityForm({ provider, directory, close, created }: {
    provider: MediaOpportunityProvider;
    directory: {
        accounts(): Promise<Account[]>;
        subjects(id: string): Promise<Subject[]>;
    };
    close(): void;
    created(id: string): void;
}) {
    const [accounts, setAccounts] = useState<Account[]>([]), [subjects, setSubjects] = useState<Subject[]>([]), [draft, setDraft] = useState<OpportunityDraft>({ accountId: '', subjectId: '', title: '', outletProgram: '', opportunityType: 'journalist_request', editorialRequest: '', sourceDescription: '', observedAt: new Date().toISOString().slice(0, 16) });
    useEffect(() => { void directory.accounts().then(setAccounts); }, []);
    useEffect(() => {
        if (draft.accountId)
            void directory.subjects(draft.accountId).then(setSubjects);
    }, [draft.accountId]);
    const set = (k: keyof OpportunityDraft, x: string) => setDraft(d => ({ ...d, [k]: x })), required = useMemo(() => draft.accountId && draft.subjectId && draft.title && draft.outletProgram && draft.editorialRequest && draft.sourceDescription, [draft]);
    return <section className="story-editor opportunity-editor" role="dialog" aria-modal="true">
<header>
<h2>Add sourced opportunity</h2>
<button className="button button--tertiary" onClick={close}>CLOSE</button>
</header>
<div className="moi-form-grid">
<label>Account<select autoFocus value={draft.accountId} onChange={e => set('accountId', e.target.value)}>
<option value="">Select</option>{accounts.map(a => <option value={a.id} key={a.id}>{a.name}</option>)}</select>
</label>
<label>Subject<select value={draft.subjectId} onChange={e => set('subjectId', e.target.value)}>
<option value="">Select</option>{subjects.map(s => <option value={s.id} key={s.id}>{s.display_name}</option>)}</select>
</label>
<label>Title<input value={draft.title} onChange={e => set('title', e.target.value)}/>
</label>
<label>Outlet / program<input value={draft.outletProgram} onChange={e => set('outletProgram', e.target.value)}/>
</label>
</div>
<label>Editorial request<textarea value={draft.editorialRequest} onChange={e => set('editorialRequest', e.target.value)}/>
</label>
<label>Source description<textarea value={draft.sourceDescription} onChange={e => set('sourceDescription', e.target.value)}/>
</label>
<footer>
<button className="button button--primary" disabled={!required} onClick={async () => created((await provider.create({ ...draft, observedAt: new Date(draft.observedAt).toISOString() })).id)}>SAVE UNVERIFIED OPPORTUNITY</button>
</footer>
</section>;
}
